/*
 * Index is the entry point for the app.
 * Initial routes are here and secondary routes are in TwoPanelLayout
 */
import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import 'custom-event-polyfill';
import 'url-search-params-polyfill';

// Suppress noisy React 18 deprecation warnings from react-bootstrap
const originalWarn = console.warn;
console.warn = (...args: any[]) => {
    const msg = typeof args[0] === 'string' ? args[0] : '';
    if (msg.includes('Support for defaultProps will be removed') || msg.includes('findDOMNode is deprecated')) return;
    originalWarn.apply(console, args);
};
const originalError = console.error;
console.error = (...args: any[]) => {
    const msg = typeof args[0] === 'string' ? args[0] : '';
    if (msg.includes('Support for defaultProps will be removed') || msg.includes('findDOMNode is deprecated')) return;
    originalError.apply(console, args);
};
// import project css
import 'react-toggle/style.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-redux-toastr/lib/css/react-redux-toastr.min.css';
import 'react-datetime/css/react-datetime.css';
import './index.css';
import 'moment/min/locales'; // can improve performance by importing the specific locales we want to support
import './i18n';

import * as React from 'react';
import * as ReactDOM from 'react-dom/client';
import * as serviceWorker from './registerServiceWorker';
import { reportUnfinishedCatalogDownload } from './helpers/productSyncDiagnostics';
import { capturePreviousPageExit, installPageExitTracking } from './helpers/pageExit';

import {
    faBars,
    faBuilding,
    faCalendarCheck,
    faCheck,
    faClipboardList,
    faCog,
    faCogs,
    faCommentLines,
    faEdit,
    faExchange,
    faHospital,
    faListAlt,
    faMapMarkedAlt,
    faMinus,
    faPlus,
    faAngleDoubleRight,
    faAngleDoubleDown,
    faSignOut,
    faTh,
    faTimes,
    faSignalAltSlash,
    faSignal,
    faShieldCheck,
    faTrashAlt
} from '@fortawesome/pro-regular-svg-icons';

import {
    faCaretDown,
    faChevronDown,
    faChevronLeft,
    faChevronRight,
    faEnvelope,
    faFilter,
    faSearch,
    faShoppingCart,
    faUser,
    faUsers,
    faTools,
    faDownload,
    faEllipsisH
} from '@fortawesome/free-solid-svg-icons';

import App from './App';
import { Provider } from 'react-redux';
import SentryLogger from './helpers/SentryLogger';
import axios from 'axios';
import configureStore, { persistConfig } from './store/configureStore';
import { clearHistoricalResultID } from './actions/measurementPointResultsActions';
import { clearSelectedHistoricalWorkOrderID } from './actions/workOrderActions';
import { constants } from './constants/constants';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { library } from '@fortawesome/fontawesome-svg-core';
import { purgeStoredState } from 'redux-persist';
import { MsalProvider } from '@azure/msal-react';
import msalInstance from './components/auth/MsalInstance';

library.add(
    faCaretDown,
    faFilter,
    faTools,
    faCog,
    faCogs,
    faUsers,
    faCalendarCheck,
    faTh,
    faCheck,
    faTimes,
    faSearch,
    faHospital,
    faUser,
    faSignOut,
    faShoppingCart,
    faChevronDown,
    faChevronRight,
    faChevronLeft,
    faEnvelope,
    faListAlt,
    faPlus,
    faMinus,
    faBuilding,
    faExchange,
    faMapMarkedAlt,
    faBars,
    faEdit,
    faClipboardList,
    faCommentLines,
    faAngleDoubleRight,
    faAngleDoubleDown,
    faSignalAltSlash,
    faSignal,
    faShieldCheck,
    faDownload,
    faTrashAlt,
    faEllipsisH
);

const promiseFinally = require('promise.prototype.finally');
promiseFinally.shim(); // fix finally for Edge browsers
export let store: any;

const setVersionTimestamp = () => localStorage.setItem('versionTimestamp', new Date().getTime().toString());
const setVersionRefreshed = () => localStorage.setItem('versionRefreshed', new Date().getTime().toString());
export const needsVersionRefresh = (): boolean => {
    const versionTimestamp = Number(localStorage.getItem('versionTimestamp'));
    const versionRefreshed = Number(localStorage.getItem('versionRefreshed'));

    // yes if there is no version refresh (indicates initial time app uses this method after update)
    if (!versionRefreshed) {
        setVersionRefreshed();
        return true;
    }
    // yes if the version refreshed was before the last time an updated version was found
    if (versionRefreshed < versionTimestamp) {
        setVersionRefreshed();
        return true;
    }

    return false;
};

if (!(window !== window.parent && !window.opener)) {
    class IndexClass extends React.Component<Record<string, never>, { isRehydrated: boolean }> {
        constructor(props: any) {
            super(props);
            this.state = { isRehydrated: false };
            store = configureStore(() => {
                /*
                 * Viewing a past visit is a thing you are doing right now, not a thing the device
                 * remembers. Both IDs are inside persisted reducers, so leaving the app with a
                 * History row open used to bring it back on the next launch - and every screen
                 * that goes read only for History (MMG-4345) would come up read only, on a job
                 * that is not history at all. Clear them the moment the store rehydrates.
                 */
                store.dispatch(clearHistoricalResultID());
                store.dispatch(clearSelectedHistoricalWorkOrderID());
                // MMG-4461: if the last product download never finished, say so once (state is loaded, app not yet mounted)
                reportUnfinishedCatalogDownload(() => store.getState());
                this.setState({ isRehydrated: true });
            });
        }
        render() {
            if (!this.state.isRehydrated) {
                return null;
            }
            return (
                <ErrorBoundary>
                    <React.Suspense fallback="Loading...">
                        <Provider store={store}>
                            <MsalProvider instance={msalInstance.msalApp}>
                                <App />
                            </MsalProvider>
                        </Provider>
                    </React.Suspense>
                </ErrorBoundary>
            );
        }
    }
    /*
* flush writes the current state to storage - this will be right after USER_LOGOUT_SUCCESS is triggered
in userActions.  then we pause the persistor in order to prevent anything else from being persisted while we logout.
*/
    const handleLogout = () => {
        // persistor.flush().then(() => {
        //   persistor.pause();
        // });
        purgeStoredState(persistConfig);
    };
    document.addEventListener('userLogout', handleLogout, false);

    // set Axios default header for accepting JSON
    axios.defaults.headers.common['Accept'] = 'application/json';
    axios.defaults.headers.common['Content-Type'] = 'application/json';
    axios.defaults.timeout = constants.httpTimeout;

    // Initialize Sentry (errors and warnings only)
    SentryLogger.init();
    // MMG-4461: how did the last page end (our reload, user left, or killed)? Read before anything else can overwrite it
    capturePreviousPageExit();
    installPageExitTracking();

    const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
    root.render(<IndexClass />);

    console.log('[Beacon Update] Service worker registration starting...');

    serviceWorker.register({
        onUpdate: registration => {
            console.log('[Beacon Update] App update detected - installing new version');
            setVersionTimestamp(); // indicates timestamp when update was found
            document.dispatchEvent(new CustomEvent('newVersionAvailable'));
            if (registration && registration.waiting) {
                registration.waiting.postMessage({ type: 'SKIP_WAITING' });
            }
        },
        onSuccess: registration => {
            console.log('[Beacon Update] Service worker registered successfully');
        }
    });
}
